import React, { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCollection, addDocument, updateDocument, deleteDocument, where } from '@/hooks/useFirestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { 
  Plus, 
  Search, 
  MapPin, 
  Edit, 
  Trash2, 
  Power, 
  Monitor,
  Building2,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const LocationForm = ({ location, onSave, onClose }) => {
  const [formData, setFormData] = useState(location || {
    name: '',
    address: '',
    city: '',
    region: '',
    contactEmail: '',
    contactPhone: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Location Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Store Name"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="city">City *</Label>
          <Input
            id="city"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            placeholder="London"
            required
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="address">Address</Label>
        <Input
          id="address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="123 High Street"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="region">Region</Label>
          <Input
            id="region"
            value={formData.region}
            onChange={(e) => setFormData({ ...formData, region: e.target.value })}
            placeholder="South East"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select 
            value={formData.status} 
            onValueChange={(value) => setFormData({ ...formData, status: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="contactEmail">Contact Email</Label>
          <Input
            id="contactEmail"
            type="email"
            value={formData.contactEmail}
            onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
            placeholder="manager@store.com"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contactPhone">Contact Phone</Label>
          <Input
            id="contactPhone"
            value={formData.contactPhone}
            onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
            placeholder="+44 123 456 7890"
          />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : (location ? 'Update' : 'Create')} Location
        </Button>
      </DialogFooter>
    </form>
  );
};

const Locations = () => {
  const { userProfile, isHQ } = useAuth();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [disableConfirm, setDisableConfirm] = useState(null);
  const [disableCode, setDisableCode] = useState('');

  const locationsQuery = isHQ ? [] : [where('id', 'in', userProfile?.assignedLocations || ['none'])];
  const { documents: locations, loading } = useCollection('locations', locationsQuery);
  const { documents: screens } = useCollection('screens');

  // Get unique cities
  const cities = useMemo(() => {
    const uniqueCities = [...new Set(locations.map(l => l.city).filter(Boolean))];
    return uniqueCities.sort();
  }, [locations]);

  // Filter and group locations
  const filteredLocations = useMemo(() => {
    return locations.filter(loc => {
      const matchesSearch = loc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           loc.address?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCity = selectedCity === 'all' || loc.city === selectedCity;
      return matchesSearch && matchesCity;
    }).sort((a, b) => (a.city || '').localeCompare(b.city || '') || (a.name || '').localeCompare(b.name || ''));
  }, [locations, searchTerm, selectedCity]);

  // Group by city
  const locationsByCity = useMemo(() => {
    const grouped = {};
    filteredLocations.forEach(loc => {
      const city = loc.city || 'Uncategorized';
      if (!grouped[city]) grouped[city] = [];
      grouped[city].push(loc);
    });
    return grouped;
  }, [filteredLocations]);

  const handleSaveLocation = async (data) => {
    if (editingLocation) {
      await updateDocument('locations', editingLocation.id, data);
      toast({ title: 'Location updated successfully' });
    } else {
      await addDocument('locations', data);
      toast({ title: 'Location created successfully' });
    }
    setEditingLocation(null);
  };

  const handleDisableLocation = async () => {
    if (disableCode !== '1234') {
      toast({ title: 'Invalid confirmation code', variant: 'destructive' });
      return;
    }
    const newStatus = disableConfirm.status === 'active' ? 'inactive' : 'active';
    await updateDocument('locations', disableConfirm.id, { status: newStatus });
    
    // Update all screens for this location to maintenance mode if disabling
    if (newStatus === 'inactive') {
      const locationScreens = screens.filter(s => s.locationId === disableConfirm.id);
      for (const screen of locationScreens) {
        await updateDocument('screens', screen.id, { status: 'maintenance' });
      }
    }
    
    toast({ title: `Location ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully` });
    setDisableConfirm(null);
    setDisableCode('');
  };

  const handleDeleteLocation = async (locationId) => {
    await deleteDocument('locations', locationId);
    toast({ title: 'Location deleted successfully' });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Locations</h1>
          <p className="text-slate-400">Manage your store locations</p>
        </div>
        {isHQ && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingLocation(null)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700">
              <DialogHeader>
                <DialogTitle>{editingLocation ? 'Edit' : 'Add New'} Location</DialogTitle>
                <DialogDescription>Enter the location details below</DialogDescription>
              </DialogHeader>
              <LocationForm 
                location={editingLocation} 
                onSave={handleSaveLocation} 
                onClose={() => { setDialogOpen(false); setEditingLocation(null); }}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700"
          />
        </div>
        <Select value={selectedCity} onValueChange={setSelectedCity}>
          <SelectTrigger className="w-48 bg-slate-800/50 border-slate-700">
            <SelectValue placeholder="Filter by city" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {cities.map(city => (
              <SelectItem key={city} value={city}>{city}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Locations Grid */}
      <ScrollArea className="h-[calc(100vh-280px)]">
        {Object.keys(locationsByCity).length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building2 className="w-12 h-12 text-slate-500 mb-4" />
              <p className="text-slate-400">No locations found</p>
              {isHQ && <p className="text-sm text-slate-500">Click "Add Location" to create your first location</p>}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(locationsByCity).map(([city, cityLocations]) => (
              <div key={city}>
                <h3 className="text-lg font-semibold text-slate-300 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {city}
                  <Badge variant="secondary" className="ml-2">{cityLocations.length}</Badge>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cityLocations.map(location => {
                    const locationScreens = screens.filter(s => s.locationId === location.id);
                    const onlineCount = locationScreens.filter(s => s.status === 'online').length;
                    
                    return (
                      <Card key={location.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-white text-lg">{location.name}</CardTitle>
                              <CardDescription>{location.address}</CardDescription>
                            </div>
                            <Badge 
                              className={`${location.status === 'active' ? 'bg-green-500/20 text-green-400' : location.status === 'maintenance' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-600 text-slate-300'}`}
                            >
                              {location.status || 'active'}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-4 mb-4">
                            <div className="flex items-center gap-2 text-sm text-slate-400">
                              <Monitor className="w-4 h-4" />
                              <span>{locationScreens.length} screens</span>
                            </div>
                            <div className="text-sm">
                              <span className="text-green-400">{onlineCount} online</span>
                              <span className="text-slate-500 mx-1">•</span>
                              <span className="text-red-400">{locationScreens.length - onlineCount} offline</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Link to={`/locations/${location.id}`} className="flex-1">
                              <Button variant="secondary" className="w-full" size="sm">
                                View Details
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            </Link>
                            {isHQ && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => { setEditingLocation(location); setDialogOpen(true); }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className={location.status === 'active' ? 'text-amber-400 hover:text-amber-300' : 'text-green-400 hover:text-green-300'}
                                  onClick={() => setDisableConfirm(location)}
                                >
                                  <Power className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Disable Confirmation Dialog */}
      <AlertDialog open={!!disableConfirm} onOpenChange={() => { setDisableConfirm(null); setDisableCode(''); }}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {disableConfirm?.status === 'active' ? 'Disable' : 'Enable'} Location
            </AlertDialogTitle>
            <AlertDialogDescription>
              {disableConfirm?.status === 'active' 
                ? 'This will put all screens at this location into maintenance mode. Enter the confirmation code (1234) to proceed.'
                : 'This will re-enable the location. Enter the confirmation code (1234) to proceed.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            placeholder="Enter confirmation code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            className="bg-slate-900/50 border-slate-700"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisableLocation}>
              {disableConfirm?.status === 'active' ? 'Disable' : 'Enable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Locations;
